import { qoursesApi } from '@/api/qourses.tsx'
import { InstructorName } from '@/components/Resolvers.tsx'
import { InstructorSelection } from '@/components/entityDropdowns/InstructorSelection.tsx'
import { LocationSelection } from '@/components/entityDropdowns/LocationSelection.tsx'
import Dynamic from '@/components/modals/dynamic.tsx'
import useGetCalendars from '@/hooks/calendars/useGetCalendars.tsx'
import { Badge } from '@/shadcn/components/ui/badge.tsx'
import { Button } from '@/shadcn/components/ui/button.tsx'
import { DialogDescription, DialogHeader, DialogTitle } from '@/shadcn/components/ui/dialog.tsx'
import { Input } from '@/shadcn/components/ui/input.tsx'
import { ScrollArea } from '@/shadcn/components/ui/scroll-area.tsx'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/shadcn/components/ui/select.tsx'
import { ToastVariant, sendNotification } from '@/utils.tsx'
import { ChevronRight, Cog, Copy } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import {
  CalendarEntity,
  InstructorEntity,
  LocationEntity,
  PostCalendarDto,
} from '../../../qourses-api-client'
import privilege = PostCalendarDto.privilege

export default function BackofficeAddMeetingsToCalendarModal() {
  const { t: translate } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL
  const [loading, setLoading] = useState(false)
  const [manualCalendarUrl, setManualCalendarUrl] = useState<string | null>(null)

  const [selectedInstructor, setSelectedInstructor] = useState<InstructorEntity | null>(null)
  const [selectedLocation, setSelectedLocation] = useState<LocationEntity | null>(null)
  const [selectedPrivilege, setSelectedPrivilege] = useState<privilege | null>(privilege.FULL)

  const {
    calendars,
    isLoading: isLoadingCalendars,
    isError: isErrorCalendars,
    refetch,
  } = useGetCalendars()

  const isMobile = useMediaQuery({ query: '(max-width: 750px)' })

  const handleCreateCalendar = async (
    calendarProvider: 'apple' | 'google' | 'outlook' | 'manual',
  ) => {
    setLoading(true)

    try {
      let calendar: CalendarEntity

      if (selectedInstructor) {
        calendar = await qoursesApi.calendar.calendarControllerCreateCalendarV1(
          {
            instructorId: selectedInstructor.id,
            privilege: selectedPrivilege,
          },
          'false',
        )
      } else if (selectedLocation) {
        calendar = await qoursesApi.calendar.calendarControllerCreateCalendarV1(
          {
            locationId: selectedLocation.id,
            privilege: selectedPrivilege,
          },
          'false',
        )
      } else {
        calendar = await qoursesApi.calendar.calendarControllerCreateCalendarV1(
          {
            privilege: selectedPrivilege,
          },
          'false',
        )
      }

      // Validate that calendar was created successfully and has a token
      if (!calendar || !calendar.token) {
        sendNotification(
          translate('modals.backofficeAddMeetingsToCalendarModal.notifications.error.title'),
          translate('modals.backofficeAddMeetingsToCalendarModal.notifications.error.subtitle'),
          ToastVariant.Error,
        )
        setLoading(false)
        return
      }

      const calendarUrl = `${apiUrl}/v1/public/calendar/${calendar.token}/${DateTime.now().plus({ years: 3 }).toISODate()}`

      if (calendarProvider === 'apple') {
        const webcalUrl = calendarUrl.replace('https://', 'webcal://')
        if (isMobile) {
          setTimeout(() => {
            window.open(`${webcalUrl}`, '_blank')
          })
        } else {
          setTimeout(() => {
            window.open(`${webcalUrl}`, 'noopener,noreferrer')
          })
        }
      }

      if (calendarProvider === 'google') {
        const webcalUrl = calendarUrl.replace('https://', 'webcal://')
        setTimeout(() => {
          window.open(`https://calendar.google.com/calendar/r?cid=${webcalUrl}`, '_blank')
        })
      }

      if (calendarProvider === 'outlook') {
        setTimeout(() => {
          window.open(
            `https://outlook.live.com/calendar/addcalendar?url=${calendarUrl}&name=Qourses`,
          )
        })
      }

      if (calendarProvider === 'manual') {
        setManualCalendarUrl(calendarUrl)
      }

      refetch()
    } catch (error) {
      console.error(error)
      sendNotification(
        translate('modals.backofficeAddMeetingsToCalendarModal.notifications.error.title'),
        translate('modals.backofficeAddMeetingsToCalendarModal.notifications.error.subtitle'),
        ToastVariant.Error,
      )
    } finally {
      setLoading(false)
    }
  }

  const handleArchiveCalendar = async (calendarId: string) => {
    try {
      await qoursesApi.calendar.calendarControllerArchiveCalendarV1(calendarId, 'false')
      refetch()
    } catch (error) {
      console.error(error)
    }
  }

  const handleCopyManualCalendarUrl = () => {
    if (manualCalendarUrl) {
      navigator.clipboard.writeText(manualCalendarUrl)
      sendNotification(
        translate('modals.backofficeAddMeetingsToCalendarModal.notifications.manualCopy.title'),
        translate('modals.backofficeAddMeetingsToCalendarModal.notifications.manualCopy.subtitle'),
        ToastVariant.Info,
      )
    }
  }

  return (
    <Dynamic.Content className="p-4 sm:p-8 max-h-[85vh] h-full sm:h-auto overflow-auto">
      <DialogHeader className="mb-2">
        <DialogTitle className="flex flex-col gap-y-2 sm:mt-2">
          <p className="mt-4">{translate('modals.backofficeAddMeetingsToCalendarModal.title')}</p>
        </DialogTitle>
        <DialogDescription>
          {translate('modals.backofficeAddMeetingsToCalendarModal.subtitle')}
        </DialogDescription>
      </DialogHeader>

      <ScrollArea className="p-2 h-auto" type={'always'}>
        <div>
          <div className="text-sm mb-2">
            {translate('modals.backofficeAddMeetingsToCalendarModal.privilege.label')}
          </div>
          <div className="mt-2 mb-2">
            <Select
              key="privilege"
              onValueChange={(value) => setSelectedPrivilege(value as privilege)}
              value={selectedPrivilege ?? undefined}
            >
              <SelectTrigger className="bg-white">
                <SelectValue
                // placeholder={translate('dropdowns.instructors.placeholder')}
                // value={selectedPrivilege}
                ></SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>{translate('dropdowns.calendars.privileges.title')}</SelectLabel>
                  <SelectItem value={privilege.FULL}>
                    {translate('dropdowns.calendars.privileges.FULL')}
                  </SelectItem>
                  <SelectItem value={privilege.LIMITED}>
                    {translate('dropdowns.calendars.privileges.LIMITED')}
                  </SelectItem>
                  <SelectItem value={privilege.MINIMAL}>
                    {translate('dropdowns.calendars.privileges.MINIMAL')}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm mb-2">
            {translate('modals.backofficeAddMeetingsToCalendarModal.instructor.label')}
            <Badge variant="indigoBezel" className="ml-2">
              Optional
            </Badge>
          </div>
          <div className="mt-2 mb-2">
            <InstructorSelection setSelectedInstructor={setSelectedInstructor} />
          </div>
          <div className="text-sm mb-2">
            {translate('modals.backofficeAddMeetingsToCalendarModal.location.label')}
            <Badge variant="indigoBezel" className="ml-2">
              Optional
            </Badge>
          </div>
          <LocationSelection setSelectedLocation={setSelectedLocation} />
        </div>

        <Button
          variant="outline"
          className="mt-3 w-full"
          onClick={() => handleCreateCalendar('apple')}
          disabled={loading}
        >
          <img className="inline-block size-6 p-1 mr-1" src="/logos/apple.svg" alt="Apple Logo" />
          {translate('modals.backofficeAddMeetingsToCalendarModal.buttonApple')}
        </Button>
        <Button
          variant="outline"
          className="mt-3 w-full"
          onClick={() => handleCreateCalendar('google')}
          disabled={loading}
        >
          <img className="inline-block size-6 p-1 mr-1" src="/logos/google.svg" alt="Google Logo" />
          {translate('modals.backofficeAddMeetingsToCalendarModal.buttonGoogle')}
        </Button>
        <Button
          variant="outline"
          className="mt-3 mb-2 sm:mb-0 w-full"
          onClick={() => handleCreateCalendar('outlook')}
          disabled={loading}
        >
          <img
            className="inline-block size-6 p-1 mr-1"
            src="/logos/microsoft.svg"
            alt="Microsoft Logo"
          />
          {translate('modals.backofficeAddMeetingsToCalendarModal.buttonOutlook')}
        </Button>
        {manualCalendarUrl ? (
          <div className="flex gap-2 sm:mt-3">
            <Input readOnly value={manualCalendarUrl} className="flex-1 bg-white" />
            <Button variant="outline" onClick={handleCopyManualCalendarUrl}>
              <Copy className="inline-block size-4 mr-1" />
              {translate('modals.backofficeAddMeetingsToCalendarModal.buttonCopy') || 'Copy'}
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            className="flex flex-1 w-full mt-3"
            onClick={() => handleCreateCalendar('manual')}
            disabled={loading}
          >
            <Cog className="inline-block size-6 p-1 mr-1" />
            {translate('modals.backofficeAddMeetingsToCalendarModal.buttonManual')}
          </Button>
        )}

        {calendars.length > 0 && (
          <>
            <div className="relative mt-4 mb-2">
              <div aria-hidden="true" className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-2 font-semibold text-muted-foreground">
                  {translate('modals.backofficeAddMeetingsToCalendarModal.setupCalendarsTitle')}
                </span>
              </div>
            </div>
            <div className="relative bg-gray-50 p-2 ring-1 ring-gray-200 rounded-lg shadow-inner max-h-32 overflow-y-auto">
              {!isLoadingCalendars &&
                !isErrorCalendars &&
                calendars.map((calendar) => (
                  <div
                    key={calendar.id}
                    className="mt-2 bg-white text-sm flex gap-x-1 p-2 border rounded-md items-center justify-between first:mt-2 last:mb-2"
                  >
                    <div>
                      <p>{translate('modals.backofficeAddMeetingsToCalendarModal.lastSynced')}</p>
                      <p className="font-semibold text-xs">
                        {calendar.lastUsageDay !== null
                          ? DateTime.fromISO(calendar.lastUsageDay).toLocaleString(
                              DateTime.DATE_FULL,
                            )
                          : translate('modals.backofficeAddMeetingsToCalendarModal.syncPending')}
                      </p>
                      {calendar.instructorId && (
                        <div className="text-xs mt-1">
                          {translate(
                            'modals.backofficeAddMeetingsToCalendarModal.instructor.calendarLabel',
                          )}
                          <InstructorName instructorId={calendar.instructorId} />
                        </div>
                      )}
                    </div>
                    <div
                      className="cursor-pointer text-xs underline mt-1 flex items-center"
                      onClick={() => handleArchiveCalendar(calendar.id)}
                    >
                      {translate('modals.backofficeAddMeetingsToCalendarModal.archiveCalendar')}
                      <ChevronRight className="ml-1 size-3" />
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}
      </ScrollArea>
    </Dynamic.Content>
  )
}
